import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { ChangePasswordRequest } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input, Card } from '@/components/ui';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import toast from 'react-hot-toast';

// Styled components for the new password change design
const ChangePasswordContainer = styled(Box)(({ theme }) => ({
  //display: 'flex',
  //flexDirection: 'column',
  //maxWidth: 700,
  margin: '0 auto',
  width: '100%',
  height: '100%',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    maxWidth: 320,
    padding: theme.spacing(0),
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: 420,
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 700,
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '100%',
    width: '100%',
  },
}));

const PasswordCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '600px',
  margin: '0 auto',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const PasswordCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const RequirementBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f9ff',
  border: '1px solid #e0e8f5',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const ActionButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  width: '100%',

  // Mobile: stack buttons vertically and make them full width
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    '& > *': {
      width: '100%',
      maxWidth: '100%',
      flex: '0 0 auto',
    },
  },

  // Tablet and up: horizontal layout, buttons keep intrinsic width and centered
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    '& > *': {
      flex: '0 0 auto',    // don't force equal widths on larger screens
      minWidth: 140,
      maxWidth: 220,
      width: 'auto',
    },
  },

  // Desktop and large screens: slightly larger buttons
  [theme.breakpoints.up('md')]: {
    '& > *': {
      minWidth: 160,
      maxWidth: 240,
    },
  },
}));

// Validation schema
const changePasswordSchema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    )
    .notOneOf([yup.ref('currentPassword')], 'New password must be different from current password'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

// Initial form values
const initialValues: ChangePasswordRequest = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export const ChangePasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const { changePassword, loading } = useAuth();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle form submission
  const handleSubmit = async (values: ChangePasswordRequest, { setFieldError }: { setFieldError: (field: string, message: string) => void }) => {

    const loadingToast = toast.loading(
      'Welcome back! Getting everything ready for you…',
      {
        position: 'top-center',
        icon: (
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Loading_2.gif?20170503175831"
            alt="loading"
            style={{
              width: '50px',   // change size here
              height: '50px',
              objectFit: 'contain',
              marginRight: '8px'
            }}
          />
        ),
        style: {
          marginTop: '40vh',
          transform: 'translateY(-50%)',

          background: '#ffffff',
          color: '#000',
          padding: '18px 24px',
          borderRadius: '12px',
          width: '620px',
          textAlign: 'center',

          borderTop: '4px solid #17A6DA',
          fontSize: '18px',
          fontWeight: 500,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
      }
    );
    try {
      await changePassword(values);
      toast.dismiss(loadingToast);
      // On success, redirect to dashboard
      // navigate('/dashboard', {
      //   replace: true,
      // });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password change failed';
      setFieldError('currentPassword', errorMessage);
    }
  };

  return (
    <ChangePasswordContainer>
      {/* Password Card */}
      <PasswordCard>
        <PasswordCardHeader>
          <LockIcon sx={{ color: '#013d6e', mr: 1.5, fontSize: '1.75rem' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Change Password
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Enter your current and new password
            </Typography>
          </Box>
        </PasswordCardHeader>

        <Formik
          initialValues={initialValues}
          validationSchema={changePasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Current Password Field */}
                <Box>
                  <Input
                    fullWidth
                    label="Current Password *"
                    name="currentPassword"
                    type="password"
                    placeholder="Enter Current Password"
                    value={values.currentPassword}
                    onChange={e => setFieldValue('currentPassword', e.target.value)}
                    onBlur={() => setFieldTouched('currentPassword', true)}
                    onFocus={() => {
                      if (!touched.currentPassword) {
                        setFieldTouched('currentPassword', true);
                      }
                    }}
                    error={touched.currentPassword && !!errors.currentPassword}
                    helperText={touched.currentPassword && errors.currentPassword}
                    disabled={loading}
                    size="small"
                    variant="outlined"
                    showPassword={showCurrentPassword}
                    onPasswordToggle={() => setShowCurrentPassword(!showCurrentPassword)}
                  />
                </Box>

                {/* New Password Field */}
                <Box>
                  <Input
                    fullWidth
                    label="New Password *"
                    name="newPassword"
                    type="password"
                    placeholder="Enter New Password"
                    value={values.newPassword}
                    onChange={e => setFieldValue('newPassword', e.target.value)}
                    onBlur={() => setFieldTouched('newPassword', true)}
                    onFocus={() => {
                      if (!touched.newPassword) {
                        setFieldTouched('newPassword', true);
                      }
                    }}
                    error={touched.newPassword && !!errors.newPassword}
                    helperText={touched.newPassword && errors.newPassword}
                    disabled={loading}
                    size="small"
                    variant="outlined"
                    showPassword={showNewPassword}
                    onPasswordToggle={() => setShowNewPassword(!showNewPassword)}
                  />
                </Box>

                {/* Confirm Password Field */}
                <Box>
                  <Input
                    fullWidth
                    label="Confirm New Password *"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm New Password"
                    value={values.confirmPassword}
                    onChange={e => setFieldValue('confirmPassword', e.target.value)}
                    onBlur={() => setFieldTouched('confirmPassword', true)}
                    onFocus={() => {
                      if (!touched.confirmPassword) {
                        setFieldTouched('confirmPassword', true);
                      }
                    }}
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    disabled={loading}
                    size="small"
                    variant="outlined"
                    showPassword={showConfirmPassword}
                    onPasswordToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                </Box>

                {/* Action Buttons */}
                <ActionButtonContainer>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/dashboard')}
                    sx={{
                      flex: 1,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      maxWidth: '180px',
                      borderRadius: '40px',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="small"
                    loading={loading}
                    loadingText="Changing password..."
                    disabled={
                      !values.currentPassword || !values.newPassword || !values.confirmPassword
                    }
                    sx={{
                      flex: 1,
                      fontSize: '1rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      maxWidth: '180px',
                      borderRadius: '40px',
                      backgroundColor: '#013d6e',
                      '&:hover': {
                        backgroundColor: '#013d6e',
                      },
                    }}
                  >
                    Change Password
                  </Button>
                </ActionButtonContainer>
              </Box>
            </Form>
          )}
        </Formik>
      </PasswordCard>
    </ChangePasswordContainer>
  );
};

export default ChangePasswordForm;
